import { FC, useState } from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { EdgarIssuerName } from 'api/models/EdgarIssuerName';
import { EdgarIssuerNameSuggestion } from 'api/models/EdgarIssuerNameSuggestion';
import { useProfileEdgarIssuerNames, useSearchEdgarIssuerNames } from 'services/hooks/companies';
import { EdgarIssuerNamesSectionProps } from './EdgarIssuerNamesSection.props';

const EdgarIssuerNamesSection: FC<EdgarIssuerNamesSectionProps> = ({
  profileId,
  onPendingChange,
  suggestedItems = [],
}) => {
  const [pickerSearch, setPickerSearch] = useState('');
  const [pendingAddItems, setPendingAddItems] = useState<(EdgarIssuerName | EdgarIssuerNameSuggestion)[]>([]);
  const [pendingRemoveIds, setPendingRemoveIds] = useState<number[]>([]);
  const [dismissedSuggestionIds, setDismissedSuggestionIds] = useState<Set<number>>(new Set());

  const { data: linkedData, isLoading: linkedLoading } = useProfileEdgarIssuerNames(profileId);
  const { data: searchData, isLoading: searchLoading } = useSearchEdgarIssuerNames(pickerSearch);

  const linked: EdgarIssuerName[] = linkedData?.results ?? [];
  const pendingAddIds = pendingAddItems.map(i => i.id!);

  const activeSuggestions = suggestedItems.filter(
    s => s.id != null &&
      !dismissedSuggestionIds.has(s.id) &&
      !pendingAddIds.includes(s.id) &&
      !linked.some(l => l.id === s.id),
  );

  const searchResults: EdgarIssuerName[] = (searchData?.results ?? []).filter(
    i => !pendingAddIds.includes(i.id!) && !linked.some(l => l.id === i.id),
  );

  const notify = (nextAddItems: typeof pendingAddItems, nextRemoveIds: number[]) => {
    onPendingChange?.(nextAddItems.map(i => i.id!), nextRemoveIds);
  };

  const handleAdd = (issuer: EdgarIssuerName | EdgarIssuerNameSuggestion) => {
    // If it was previously pending removal, undo that instead
    if (pendingRemoveIds.includes(issuer.id!)) {
      const nextRemove = pendingRemoveIds.filter(id => id !== issuer.id);
      setPendingRemoveIds(nextRemove);
      notify(pendingAddItems, nextRemove);
    } else {
      const nextAdd = [...pendingAddItems, issuer];
      setPendingAddItems(nextAdd);
      notify(nextAdd, pendingRemoveIds);
    }
    setPickerSearch('');
  };

  const handleRemove = (issuer: EdgarIssuerName) => {
    if (profileId == null || pendingAddIds.includes(issuer.id!)) {
      // Pending add — just remove from pending list
      const nextAdd = pendingAddItems.filter(i => i.id !== issuer.id);
      setPendingAddItems(nextAdd);
      notify(nextAdd, pendingRemoveIds);
    } else {
      // Currently linked — mark as pending removal
      const nextRemove = [...pendingRemoveIds, issuer.id!];
      setPendingRemoveIds(nextRemove);
      notify(pendingAddItems, nextRemove);
    }
  };

  const handleAcceptSuggestion = (suggestion: EdgarIssuerNameSuggestion) => {
    setDismissedSuggestionIds(prev => new Set([...prev, suggestion.id!]));
    handleAdd(suggestion);
  };

  const handleDismissSuggestion = (suggestion: EdgarIssuerNameSuggestion) => {
    setDismissedSuggestionIds(prev => new Set([...prev, suggestion.id!]));
  };

  // Display = currently linked (excluding pending removals) + pending additions
  const displayItems: (EdgarIssuerName | EdgarIssuerNameSuggestion)[] = [
    ...linked.filter(i => !pendingRemoveIds.includes(i.id!)),
    ...pendingAddItems.filter(i => !linked.some(l => l.id === i.id)),
  ];

  return (
    <Box>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        SEC / Edgar Issuer Names
      </Typography>

      {activeSuggestions.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <AutoAwesomeIcon sx={{ fontSize: 12 }} />
            AI suggestions — click to add
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {activeSuggestions.map(s => (
              <Chip
                key={s.id}
                label={`${s.edgar_name} (${s.fund_name})`}
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => handleAcceptSuggestion(s)}
                onDelete={() => handleDismissSuggestion(s)}
              />
            ))}
          </Box>
        </Box>
      )}

      {linkedLoading && profileId != null && <CircularProgress size={20} />}
      {!linkedLoading && displayItems.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No issuer names linked.
        </Typography>
      )}
      {!linkedLoading && displayItems.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          {displayItems.map(issuer => (
            <Chip
              key={issuer.id}
              label={`${issuer.edgar_name} (${issuer.fund_name})`}
              size="small"
              onDelete={() => handleRemove(issuer as EdgarIssuerName)}
              deleteIcon={<Tooltip title="Remove"><LinkOffIcon /></Tooltip>}
            />
          ))}
        </Box>
      )}

      <TextField
        placeholder="Search issuer names to link…"
        size="small"
        fullWidth
        value={pickerSearch}
        onChange={e => setPickerSearch(e.target.value)}
        sx={{ mt: 1 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchLoading ? (
              <InputAdornment position="end">
                <CircularProgress size={16} />
              </InputAdornment>
            ) : null,
          },
        }}
      />

      {pickerSearch.length > 0 && (
        <Paper variant="outlined" sx={{ mt: 0.5, maxHeight: 200, overflow: 'auto' }}>
          {searchResults.length === 0 && !searchLoading ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
              No unlinked issuer names found.
            </Typography>
          ) : (
            <List dense disablePadding>
              {searchResults.map(issuer => (
                <ListItemButton key={issuer.id} onClick={() => handleAdd(issuer)}>
                  <ListItemText
                    primary={issuer.edgar_name}
                    secondary={`${issuer.fund_name} · ${issuer.common_profile != null ? 'linked to another profile' : 'unlinked'}`}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default EdgarIssuerNamesSection;
